import { buyerActionFactsOf } from '../../slices/marketplace/timeline/utils/buyer-action-facts';
import { EngagementBuyerActionState } from '../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { MeetingParticipantsType } from '../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { EngagementMeetingStatus } from '../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { EngagementMeeting } from '../../slices/marketplace/meetings/models/get-engagement-conference';
import { DocumentDetails } from '../../slices/marketplace/timeline/models/document-details.model';
import { EngagementData } from '../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../slices/marketplace/timeline/models/engagement-process.model';
import { ActionDocumentType } from '../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { DocumentDetailsStatus } from '../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { EngagementDocumentSource } from '../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { TimelineActionStatus } from '../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineAction } from '../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { isFlowOfFundsAvailable } from '../../utils/flowOfFundsAccess';

export const findBuyerDdNdaDocument = (
    linkedAction: TimelineAction | undefined,
    engagementData: EngagementData | undefined,
    engagementBuyerId: number | null | undefined
): DocumentDetails | undefined => {
    if (!linkedAction?.loiAndDdNdaExecutionConfigId || engagementBuyerId == null) {
        return undefined;
    }

    const config = engagementData?.loiAndDdNdaExecutionConfigList?.find(
        (c) => c.id === linkedAction.loiAndDdNdaExecutionConfigId
    );
    if (!config?.ddNdaSectionId) {
        return undefined;
    }

    return engagementData.documentsList
        ?.filter(
            (d) =>
                d.engagementBuyerId === engagementBuyerId &&
                d.sectionId === config.ddNdaSectionId &&
                d.templateId === config.ddNdaTemplateId
        )
        .sort(
            (a, b) =>
                new Date(b.datePosted ?? 0).getTime() -
                new Date(a.datePosted ?? 0).getTime()
        )[0];
};

export const isDdNdaDocumentSignedByBuyer = (
    document: DocumentDetails
): boolean =>
    document.counterParty === TimelineParty.Seller ||
    document.status === DocumentDetailsStatus.Completed;

// A range-of-value event is DFM-gated when it sits after the Detailed Financial Model selection
// event on the timeline — only buyers who purchased the DFM may submit on it (the ValueRangeModal
// shows a purchase-required message to everyone else; the server rejects direct submissions).
// Position is judged by the events' order in the payload (the API serves them in canonical
// timeline order), matching Timeline.IsRangeOfValueActionDfmGated on the backend.
export const isDfmGatedRangeEvent = (
    event: TimelineEvent,
    events: TimelineEvent[]
): boolean => {
    if (
        event.linkedAction?.actionType !==
        TimelineActionType.BuyerExpressionOfInterestWithRange
    ) {
        return false;
    }

    const dfmIndex = events.findIndex(
        (e) =>
            e.linkedAction?.actionType ===
            TimelineActionType.SellerBuyerSelectionDetailedFinancialModel
    );
    if (dfmIndex < 0) {
        return false;
    }

    const eventIndex = events.findIndex((e) => e.id === event.id);
    return eventIndex > dfmIndex;
};

export const isEventActionClickable = (
    eventid: string,
    isReadOnly: boolean,
    engagementData: EngagementData,
    events: TimelineEvent[],
    party: TimelineParty,
    buyerActionState?: EngagementBuyerActionState[],
    engagementProcess?: EngagementProcess,
    engagementBuyerId?: number | null,
    isFlowOfFundsAvailableForViewer?: boolean
): boolean => {
    if (isReadOnly && party !== TimelineParty.Observer) {
        return false;
    }

    const event = events.find((event) => event.id === eventid);
    if (event == null) {
        return false;
    }

    if (!event?.linkedAction) return false;

    const matchingParty = event.party == party;
    const sellerViewingSeller = matchingParty && party === TimelineParty.Seller;
    const isEventUpcoming = event.status === TimelineEventStatus.Upcoming;
    const isBrokerOrObserver = party === TimelineParty.Broker || party === TimelineParty.Observer;

    if (
        event.linkedAction?.actionType ==
        TimelineActionType.SellerEngagementDocument
    ) {
        const canEngagementDocBeViewed =
            party === TimelineParty.VetValue ||
            isBrokerOrObserver ||
            (sellerViewingSeller &&
                !isEventUpcoming &&
                doesEventActionDocDetailsContainContent(
                    event?.linkedAction?.referenceItemId,
                    engagementData
                ));
        return canEngagementDocBeViewed;
    }

    if (
        event.linkedAction?.actionType == TimelineActionType.BuyerReviewDocument
    ) {
        const canBuyerReviewDocBeViewed =
            (party === TimelineParty.VetValue ||
                (party === TimelineParty.Buyer && !isEventUpcoming)) &&
            isBuyerReviewDocReady(event?.linkedAction, engagementData, events);

        return canBuyerReviewDocBeViewed;
    }

    if (
        NdaTermSelectionOptionsList.some(
            (option) => option.value === event.linkedAction?.actionType
        )
    ) {
        return party === TimelineParty.VetValue || !isEventUpcoming;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms
    ) {
        if (isEventUpcoming) return false;
        if (party === TimelineParty.VetValue) return true;
        if (party === TimelineParty.Buyer) {
            const ddNdaDocument = findBuyerDdNdaDocument(
                event.linkedAction,
                engagementData,
                engagementBuyerId
            );
            if (!ddNdaDocument) {
                return false;
            }
            return (
                ddNdaDocument.counterParty === TimelineParty.Buyer ||
                ddNdaDocument.status === DocumentDetailsStatus.Completed
            );
        }
        if (party !== TimelineParty.Seller && party !== TimelineParty.Broker) {
            return false;
        }
        const engagedBuyer = engagementProcess?.buyers?.find(
            (b) => b.isLoiSelected
        );
        const actionState = engagedBuyer?.actionState?.find(
            (a) => a.actionId === event.linkedAction?.id
        );
        return !!actionState?.documentDetailsId;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerTeaserApproval
    ) {
        const canTeaserBeViewed = isEventUpcoming
            ? party === TimelineParty.VetValue || isBrokerOrObserver
            : party !== TimelineParty.Buyer;

        return canTeaserBeViewed;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerSelectionIdNda
    ) {
        if (party === TimelineParty.Seller && !isEventUpcoming) return true;
        if (party === TimelineParty.VetValue || isBrokerOrObserver) return true;

        const targetAction = buyerActionFactsOf(event.linkedAction);
        return !!targetAction;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerSelectionDetailedFinancialModel
    ) {
        if (party === TimelineParty.Seller && !isEventUpcoming) return true;
        if (party === TimelineParty.VetValue || isBrokerOrObserver) return true;

        const targetAction = buyerActionFactsOf(event.linkedAction);

        return !!targetAction;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerDataAndDocumentRequest
    ) {
        return (
            (matchingParty && !isEventUpcoming) ||
            party === TimelineParty.VetValue ||
            isBrokerOrObserver
        );
    }

    if (event.linkedAction?.actionType === TimelineActionType.SellerProfile) {
        return (
            (matchingParty && !isEventUpcoming) ||
            party === TimelineParty.VetValue ||
            isBrokerOrObserver
        );
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.BuyerExpressionOfInterestWithRange
    ) {
        const isRangeProvided =
            buyerActionFactsOf(event.linkedAction)?.status ===
            TimelineActionStatus.Completed;
        return isEventUpcoming ? false : matchingParty || isRangeProvided;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
    ) {
        return isEventUpcoming
            ? false
            : matchingParty || isBrokerOrObserver;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerProvideMeetingAvailability
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }
    if (
        event.linkedAction?.actionType === TimelineActionType.SellerBuyerMeetings
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.Buyer ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }
    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerAgreeOnClosingDate
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.Buyer ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerFlowOfFunds
    ) {
        if (isEventUpcoming) {
            return false;
        }
        return (
            isFlowOfFundsAvailableForViewer ??
            isFlowOfFundsAvailable(engagementProcess)
        );
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.BuyerLoiFormReview
    ) {
        return isEventUpcoming
            ? false
            : matchingParty
    }

    return isEventUpcoming
        ? false
        : matchingParty || party === TimelineParty.VetValue || isBrokerOrObserver;
};

export const canSellerConcludeMeetingsPeriod = (
    event: { status: TimelineEventStatus; linkedAction?: TimelineAction },
    party: TimelineParty,
    meetings: EngagementMeeting[]
): boolean => {
    if (party !== TimelineParty.Seller && party !== TimelineParty.VetValue)
        return false;
    if (
        event?.linkedAction?.actionType !==
        TimelineActionType.SellerBuyerMeetings
    ) {
        return false;
    }
    const isStepActive =
        event.status === TimelineEventStatus.InProgress ||
        event.status === TimelineEventStatus.Late;
    if (!isStepActive) return false;

    return !(meetings ?? []).some(
        (m) =>
            m.participantsType === MeetingParticipantsType.SellerBuyer &&
            (m.status === EngagementMeetingStatus.Upcoming ||
                m.status === EngagementMeetingStatus.InProgress)
    );
};

const doesEventActionDocDetailsContainContent = (
    docId: string,
    engagementData: EngagementData
): boolean => {
    const doc = engagementData.documentsList.find((doc) => doc.id === docId);

    if (doc?.type == ActionDocumentType.ForSign) {
        return true;
    }

    if (
        doc?.type == ActionDocumentType.DocumentWithFeedback &&
        doc?.source == EngagementDocumentSource.Questionnaire
    ) {
        return true;
    }

    const result = doc?.fileName != '';
    return result;
};

const isBuyerReviewDocReady = (
    action: TimelineAction,
    engagementData: EngagementData,
    events: TimelineEvent[]
) => {
    if (action?.actionType !== TimelineActionType.BuyerReviewDocument)
        return false;

    const doc = engagementData.documentsList.find(
        (doc) => doc.id === action?.referenceItemId
    );

    const isLinked = !!action?.actionReferenceId;
    const isLinkedReady =
        isLinked &&
        events.find(
            (e) =>
                e.linkedAction &&
                e.linkedAction?.id == action?.actionReferenceId
        )?.status === TimelineEventStatus.Completed;

    const result = doc?.isPrepared && (!isLinked || isLinkedReady);

    return result;
};
